/**
 * Find Your Church — what the international QR code opens.
 *
 * Written for the one situation it will actually be used in: a stranger, on a
 * phone, holding a leaflet, with no FWM account and no patience. So the search
 * box is the first thing on the page, the results are one tap from a church,
 * and nothing asks them to sign in.
 *
 * Location is asked for only when they tap the button that needs it, and the
 * page works perfectly if they say no — their coordinates are used for one
 * calculation and never sent anywhere that stores them.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Search, MapPin, Navigation, Loader2, Church, Phone, Mail, ChevronRight,
  Globe, AlertCircle,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { captureSource } from '@/lib/qrSource';

const API = process.env.REACT_APP_BACKEND_URL;

const ResultCard = ({ church }) => (
  <article
    className="group rounded-2xl border border-stone-200 bg-white p-4 transition-shadow hover:shadow-md"
    data-testid={`finder-result-${church.slug || church.id}`}
  >
    <div className="flex items-start gap-3">
      <div className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-700">
        <Church className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="truncate text-base font-semibold text-stone-900 md:text-lg">
          {church.name}
        </h3>
        <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-stone-600">
          <span className="inline-flex items-center gap-1">
            <MapPin className="h-3.5 w-3.5" />
            {[...new Set([church.city, church.region, church.country].filter(Boolean))].join(', ')}
          </span>
          {church.distance_km !== null && church.distance_km !== undefined ? (
            <Badge className="bg-emerald-600 text-white hover:bg-emerald-600"
                   data-testid="finder-distance">
              about {church.distance_km} km away
            </Badge>
          ) : null}
        </p>
        {church.address ? (
          <p className="mt-1 text-sm text-stone-500">{church.address}</p>
        ) : null}
        {church.service_times?.length ? (
          <p className="mt-1 text-sm text-stone-500">
            {church.service_times.slice(0, 2).join(' · ')}
          </p>
        ) : null}

        <div className="mt-3 flex flex-wrap gap-2">
          <Button asChild size="sm" className="min-h-[44px] rounded-full bg-stone-900 px-4 hover:bg-stone-800">
            <Link to={church.welcome_path} data-testid="finder-visit-church">
              Visit this church <ChevronRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>
          {church.contact_phone ? (
            <Button asChild size="sm" variant="outline" className="min-h-[44px] rounded-full">
              <a href={`tel:${church.contact_phone}`}><Phone className="mr-1 h-4 w-4" />Call</a>
            </Button>
          ) : null}
          {church.contact_email ? (
            <Button asChild size="sm" variant="outline" className="min-h-[44px] rounded-full">
              <a href={`mailto:${church.contact_email}`}><Mail className="mr-1 h-4 w-4" />Email</a>
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  </article>
);

export default function FindChurchPage() {
  const [q, setQ] = useState('');
  const [country, setCountry] = useState('');
  const [region, setRegion] = useState('');
  const [coords, setCoords] = useState(null);
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [data, setData] = useState(null);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);

  const moved = useMemo(
    () => new URLSearchParams(window.location.search).get('moved'), []);
  const unknown = useMemo(
    () => new URLSearchParams(window.location.search).get('unknown'), []);

  useEffect(() => { captureSource(); }, []);

  useEffect(() => {
    fetch(`${API}/api/find-church/countries`)
      .then((r) => r.json())
      .then((d) => setCountries(d.countries || []))
      .catch(() => setCountries([]));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    if (q.trim()) params.set('q', q.trim());
    if (country) params.set('country', country);
    if (region) params.set('region', region);
    if (coords) { params.set('lat', coords.lat); params.set('lng', coords.lng); }
    setLoading(true);
    const timer = setTimeout(() => {
      fetch(`${API}/api/find-church/search?${params.toString()}`)
        .then((r) => r.json())
        .then(setData)
        .catch(() => setData({ items: [], total: 0 }))
        .finally(() => setLoading(false));
    }, q ? 300 : 0);
    return () => clearTimeout(timer);
  }, [q, country, region, coords]);

  const locate = () => {
    setLocationError('');
    if (!navigator.geolocation) {
      setLocationError('This browser cannot share your location. Search by town instead.');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude.toFixed(5), lng: pos.coords.longitude.toFixed(5) });
        setLocating(false);
      },
      (err) => {
        setLocating(false);
        setLocationError(
          err.code === err.PERMISSION_DENIED
            ? 'No problem — search by town or country below instead.'
            : 'We could not work out where you are. Search by town instead.');
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 },
    );
  };

  const regions = countries.find((c) => c.country === country)?.regions || [];
  const items = data?.items || [];

  return (
    <main className="min-h-screen bg-stone-50" data-testid="find-church-page">
      <header className="bg-stone-900 px-4 pb-10 pt-8 text-white sm:px-6">
        <div className="mx-auto max-w-3xl">
          <p className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-amber-400">
            <Globe className="h-3.5 w-3.5" />Faith World Ministries
          </p>
          <h1 className="mt-2 text-4xl font-bold leading-tight sm:text-5xl">
            Find your church
          </h1>
          <p className="mt-2 max-w-xl text-base text-stone-300">
            Search for an assembly near you, anywhere in the world. No account needed.
          </p>

          <div className="mt-6 space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-stone-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Church name or town"
                className="h-14 rounded-2xl border-0 bg-white pl-11 text-base text-stone-900 placeholder:text-stone-400"
                data-testid="finder-search-input"
                aria-label="Search for a church by name or town"
              />
            </div>

            <Button
              onClick={locate}
              disabled={locating}
              className="min-h-[48px] w-full rounded-2xl bg-amber-500 text-base font-semibold text-stone-900 hover:bg-amber-400 sm:w-auto"
              data-testid="finder-near-me"
            >
              {locating
                ? <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                : <Navigation className="mr-2 h-5 w-5" />}
              {coords ? 'Using your location' : 'Find churches near me'}
            </Button>
            {locationError ? (
              <p className="text-sm text-amber-200" data-testid="finder-location-error">
                {locationError}
              </p>
            ) : null}
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6">
        {moved ? (
          <p className="mb-4 flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
             data-testid="finder-moved-notice">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            That assembly is no longer listed. Please choose another below, or contact
            the ministry office — nobody has been moved anywhere on your behalf.
          </p>
        ) : null}
        {unknown ? (
          <p className="mb-4 flex items-start gap-2 rounded-xl border border-stone-300 bg-white p-3 text-sm text-stone-700"
             data-testid="finder-unknown-notice">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            We did not recognise that code. You can still find your church below.
          </p>
        ) : null}

        {countries.length ? (
          <div className="mb-5 flex flex-wrap gap-2" data-testid="finder-countries">
            <button
              type="button"
              onClick={() => { setCountry(''); setRegion(''); }}
              className={`min-h-[44px] rounded-full border px-4 text-sm font-medium transition-colors ${
                !country ? 'border-stone-900 bg-stone-900 text-white'
                         : 'border-stone-300 bg-white text-stone-700 hover:border-stone-400'}`}
              data-testid="finder-country-all"
            >
              Everywhere
            </button>
            {countries.map((c) => (
              <button
                key={c.country}
                type="button"
                onClick={() => { setCountry(c.country); setRegion(''); }}
                className={`min-h-[44px] rounded-full border px-4 text-sm font-medium transition-colors ${
                  country === c.country ? 'border-stone-900 bg-stone-900 text-white'
                                        : 'border-stone-300 bg-white text-stone-700 hover:border-stone-400'}`}
                data-testid={`finder-country-${c.country.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {c.country} <span className="opacity-60">{c.assemblies}</span>
              </button>
            ))}
          </div>
        ) : null}

        {regions.length ? (
          <div className="mb-5 flex flex-wrap gap-2" data-testid="finder-regions">
            {regions.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setRegion(region === r ? '' : r)}
                className={`min-h-[40px] rounded-full border px-3 text-sm transition-colors ${
                  region === r ? 'border-amber-500 bg-amber-50 text-amber-900'
                               : 'border-stone-200 bg-white text-stone-600'}`}
                data-testid={`finder-region-${r.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {r}
              </button>
            ))}
          </div>
        ) : null}

        {loading ? (
          <p className="flex items-center gap-2 py-10 text-stone-500">
            <Loader2 className="h-4 w-4 animate-spin" />Looking…
          </p>
        ) : items.length ? (
          <>
            <p className="mb-3 text-sm text-stone-500" data-testid="finder-result-count">
              {items.length} {items.length === 1 ? 'church' : 'churches'}
              {data.used_location ? ', nearest first' : ''}
            </p>
            <div className="space-y-3">
              {items.map((c) => <ResultCard key={c.id} church={c} />)}
            </div>
            {data.note ? (
              <p className="mt-4 text-xs text-stone-500" data-testid="finder-position-note">
                {data.note}
              </p>
            ) : null}
          </>
        ) : (
          <div className="rounded-2xl border border-dashed border-stone-300 bg-white p-8 text-center"
               data-testid="finder-empty">
            <Church className="mx-auto h-8 w-8 text-stone-300" />
            <p className="mt-3 font-semibold text-stone-800">No churches match that search</p>
            <p className="mt-1 text-sm text-stone-500">
              Try a country above, or a different town. If you cannot find your
              assembly, the ministry office can point you to it.
            </p>
          </div>
        )}
      </div>
    </main>
  );
}
